/**
 * Manages the stacking behavior of images and it's active state animation.
 */

/**
 * Configuration object for setting default floor parameters in a stacked image manager.
 * Defines styles and behaviors for positioning and appearance.
 *
 * @typedef {Object} SetFloorParameters
 * @property {string} position - CSS position property, typically set to 'absolute'.
 * @property {string} positionLeft - Horizontal position value without a percentage sign (%).
 * @property {string} positionTop - Vertical position value without a pixel unit (px).
 * @property {string} transform - CSS transform property, used for centering.
 * @property {string} transition - CSS transition property for smooth animations.
 * @property {string} width - Width value without a pixel unit (px).
 * @property {string} cursor - CSS cursor property, typically set to 'pointer'.
 */
const setFloorParameters = {
  position: `absolute`,
  positionLeft: `50`, // Should be without %.
  positionTop: `50`, // Should be without px.
  transform: `translateX(-50%)`,
  transition: `all 0.5s ease`,
  width: `300`, // Should be without px.
  cursor: `pointer`
}

/**
 * Initializes the stacking functionality.
 *
 * @function handleStackedImages
 * @returns {void}
 */
const handleStackedImages = () => {
  setupInitialPositioning()
  assignDemoMinHeight()
}

/**
 * Sets up the initial positioning for elements with the class `.js-floor`.
 * Applies styles such as position, left, transform, top, zIndex, transition, width, and cursor
 * to each floor element based on predefined parameters and dataset attributes.
 *
 * @function
 * @returns {void}
 */
const setupInitialPositioning = () => {
  const isMobile = window.screen.width <= 768 ? true : false
  const floors = document.querySelectorAll('.js-floor')

  floors.forEach((floor, index) => {
    floor.style.position = setFloorParameters.position
    floor.style.left = `${floor.dataset.floorPositionLeft ?? setFloorParameters.positionLeft}%`
    floor.style.transform =  setFloorParameters.transform
    floor.style.top = `${ isMobile ? ( floor.dataset.floorPositionTop ?? index * setFloorParameters.positionTop ) / 2 : floor.dataset.floorPositionTop ?? index * setFloorParameters.positionTop }px`
    floor.style.zIndex = floors.length - index
    floor.style.transition = setFloorParameters.transition
    floor.style.width = `${ isMobile ? ( floor.dataset.floorWidth ?? setFloorParameters.width ) / 2 : floor.dataset.floorWidth ?? setFloorParameters.width }px`
    floor.style.cursor = setFloorParameters.cursor
  })
}

/**
 * Adjusts the minimum height of a container element to ensure it encompasses all child floor elements.
 *
 * This function calculates the total height required to fit all `.js-floor` elements within the `.js-floors` container
 * and assigns this height as the `min-height` style property of the container.
 *
 * @function
 * @throws {Error} If the `.js-floors` container element is not found in the DOM.
 */
const assignDemoMinHeight = () => {
  const isMobile = window.screen.width <= 768 ? true : false

  const floorsWrapper = document.querySelector('.js-floors')
  const floors = floorsWrapper.querySelectorAll('.js-floor')

  // Initialize with 0 height
  let maxBottom = 0

  // Iterate through all floor elements
  floors.forEach(floor => {
    // Get the position and dimensions
    const rect = floor.getBoundingClientRect()
    const bottom = rect.top + rect.height

    // Update the max bottom position if this one is larger
    if (bottom > maxBottom) {
      maxBottom = bottom
    }
  })

  // Calculate the total height (the difference between the max bottom position and the top of the container)
  const floorsWrapperRect = floorsWrapper.getBoundingClientRect()
  const totalHeight = isMobile ? ( maxBottom - floorsWrapperRect.top ) / 4 : maxBottom - floorsWrapperRect.top

  // Assign min height to wrapper.
  floorsWrapper.style.minHeight = `${totalHeight}px`
}

/**
 * Handles the expansion of a floor element based on its ID.
 *
 * @param {string} floorId - The ID of the floor to expand.
 *
 */
export const handleFloorExpand = ( floorId ) => {
  // 1. Get necessary data.
  const floors = document.querySelectorAll('.js-floor')
  const floorArray = Array.from( floors )
  const clickedFloor = floorArray.find( floor => floor.getAttribute('data-floor-id') === floorId )

  // 2. Check if is already opened -> close.
  const alreadyExpanded = handleAlreadyExpanded( clickedFloor )

  // 3. Checking if the click is on active or on other floor.
  if ( alreadyExpanded ) {
    resetActiveStack( clickedFloor )
  } else {
    // 3.1. Close all other expanded floors.
    resetAllStacks()

    // 3.2. Actually expand the floor.
    handleExpand( floorArray, clickedFloor )
  }
}

/**
 * Handles the logic for determining if a clicked floor is already expanded.
 * If the clicked floor is already active, it resets the active stack and returns `true`.
 * Otherwise, it returns `false`.
 *
 * @param {HTMLElement} clickedFloor - The DOM element representing the clicked floor.
 * @returns {boolean} - Returns `true` if the clicked floor was already expanded and is now reset, otherwise `false`.
 */
const handleAlreadyExpanded = ( clickedFloor ) => {
  const isExpanded = clickedFloor.dataset.isActive

  // If clicking the same floor again, close it.
  if ( isExpanded === 'true' ) {
    resetActiveStack( clickedFloor )
    return true
  } else {
    return false
  }
}

/**
 * Resets the active stack of floors by collapsing all floors and handling the specified floor.
 *
 * @param {HTMLElement} floor - The floor element to be handled after collapsing all floors.
 */
const resetActiveStack = ( floor ) => {
  const floors = document.querySelectorAll('.js-floor')
  const floorArray = Array.from(floors)

  handleCollapse( floors, floorArray, floor )
}

/**
 * Resets all stack elements by collapsing them.
 *
 * This function selects all elements with the class `.js-floor`, converts them into an array,
 * and iterates over each element to apply the `handleCollapse` function. The `handleCollapse`
 * function is called with the total number of floors, the array of floor elements, and the
 * current floor element.
 *
 * @returns {void} This function does not return a value.
 */
const resetAllStacks = () => {
  const floors = document.querySelectorAll('.js-floor')
  const floorArray = Array.from(floors)

  floors.forEach(( floor ) => {
    handleCollapse( floors.length, floorArray, floor )
  })

  return
}

/**
 * Handles the expansion of a clicked floor in a stacked image layout.
 * Adjusts the styles and positions of all floors relative to the clicked floor.
 *
 * @param {HTMLElement[]} floorArray - An array of floor elements in the order they are stacked.
 * @param {HTMLElement} clickedFloor - The floor element that was clicked to trigger the expansion.
 */
const handleExpand = ( floorArray, clickedFloor ) => {
  console.log( `clickedIndex: ${floorArray.indexOf(clickedFloor)}`)


  // Check which screen we set the stack for.
  const isMobile = window.screen.width <= 768 ? true : false

  // Flag that this floor is expanded.
  clickedFloor.dataset.isActive = true

  const clickedIndex = floorArray.indexOf(clickedFloor)

  floorArray.forEach((floor, index) => {
    // Reset expanded classes and styles
    floor.classList.remove('expanded')

    if (index === clickedIndex) {
      // Expand the clicked floor
      floor.classList.add('expanded')
      floor.style.width = `${ isMobile ? ( floor.dataset.floorWidth ?? setFloorParameters.width ) : (floor.dataset.floorWidth ?? setFloorParameters.width) * 2 }px`
      floor.style.height = 'auto'
      // Position at its base position
      floor.style.top = `${ isMobile ? (floor.dataset.floorPositionTop ?? index * setFloorParameters.positionTop) /2 : floor.dataset.floorPositionTop ?? index * setFloorParameters.positionTop}px`

      // Show markers
      const floorMarkers = floor.querySelectorAll('.js-marker-button')

      floorMarkers.forEach(marker => {
        marker.style.opacity = 1
        marker.style.pointerEvents = 'auto'
      })
    } else if (index < clickedIndex) {
      // Move floors above the clicked one up
      floor.style.width = `${ isMobile ? (floor.dataset.floorWidth ?? setFloorParameters.width) / 2 : floor.dataset.floorWidth ?? setFloorParameters.width }px`
      floor.style.top = `${ isMobile ? ( parseInt(floor.dataset.floorPositionTop ?? index * setFloorParameters.positionTop) - 100 ) / 2 : parseInt(floor.dataset.floorPositionTop ?? index * setFloorParameters.positionTop) - 100 }px`
    } else {
      // Move floors below the clicked one down
      floor.style.width = `${ isMobile ? (floor.dataset.floorWidth ?? setFloorParameters.width) / 2 : floor.dataset.floorWidth ?? setFloorParameters.width }px`
      floor.style.top = `${ isMobile ? ( parseInt(floor.dataset.floorPositionTop ?? index * setFloorParameters.positionTop) + 300 ) / 2 : parseInt(floor.dataset.floorPositionTop ?? index * setFloorParameters.positionTop) + 300 }px`
    }

  })
}

/**
 * Handles the collapse of a stacked image structure by resetting all floors to their original positions
 * and hiding markers on all floors.
 *
 * @param {HTMLElement[]} floors - An array of all floor elements in the stacked image structure.
 * @param {HTMLElement[]} floorArray - An array of floor elements to be iterated over for resetting styles.
 * @param {HTMLElement} clickedFloor - The floor element that was clicked to trigger the collapse.
 */
const handleCollapse = ( floors, floorArray, clickedFloor ) => {
  // Check which screen we set the stack for.
  const isMobile = window.screen.width <= 768 ? true : false

  // Flag that this floor is collapsed
  clickedFloor.dataset.isActive = false

  // Reset all floors to their original positions
  floorArray.forEach((floor, index) => {
    floor.classList.remove('expanded')
    floor.style.width = `${ isMobile ? ( floor.dataset.floorWidth ?? setFloorParameters.width ) / 2 : floor.dataset.floorWidth ?? setFloorParameters.width }px`
    floor.style.height = ''  // Reset height to default
    floor.style.zIndex = floors.length - index
    floor.style.top = `${ isMobile ? ( floor.dataset.floorPositionTop ?? index * setFloorParameters.positionTop ) / 2 : floor.dataset.floorPositionTop ?? index * setFloorParameters.positionTop }px`

    // Hide markers on all floors
    const floorMarkers = floor.querySelectorAll('.js-marker-button')
    floorMarkers.forEach(marker => {
      marker.style.opacity = 0
      marker.style.pointerEvents = 'none'
    })
  })
}

export default handleStackedImages
